/**
 * Pet data types based on Shopify custom.pet_data metafield structure
 */

export type PetSpecies = 'dog' | 'cat';
export type ActivityLevel = 'low' | 'moderate' | 'high';
export type BodyCondition = 'underweight' | 'ideal' | 'overweight';

export interface Pet {
  id: string;
  name: string;
  species: PetSpecies;
  breed: string;
  age: number;
  weight: number; // in kg
  activityLevel: ActivityLevel;
  bodyCondition: BodyCondition;
  allergies: string[];
  specialNeeds: string;
  photoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PetFormData {
  name: string;
  species: PetSpecies;
  breed: string;
  age: number;
  weight: number;
  activityLevel: ActivityLevel;
  bodyCondition: BodyCondition;
  allergies: string[];
  specialNeeds: string;
  photo?: File;
}

export interface CalorieCalculation {
  rer: number; // Resting Energy Requirement
  der: number; // Daily Energy Requirement
  recommendation: string;
}

/**
 * Calculate Resting Energy Requirement (RER)
 * Formula: RER = 70 * (weight in kg)^0.75
 */
export function calculateRER(weightKg: number): number {
  return Math.round(70 * Math.pow(weightKg, 0.75));
}

/**
 * Calculate Daily Energy Requirement (DER)
 * Based on activity level and body condition
 */
export function calculateDER(
  weightKg: number,
  activityLevel: ActivityLevel,
  bodyCondition: BodyCondition
): CalorieCalculation {
  const rer = calculateRER(weightKg);

  // Activity level multipliers
  const activityMultipliers = {
    low: 1.2,
    moderate: 1.4,
    high: 1.8,
  };

  // Body condition adjustments
  const bodyConditionAdjustments = {
    underweight: 1.2,
    ideal: 1.0,
    overweight: 0.8,
  };

  const der = Math.round(
    rer * activityMultipliers[activityLevel] * bodyConditionAdjustments[bodyCondition]
  );

  // Generate recommendation
  let recommendation = `Based on ${weightKg}kg weight, `;

  if (activityLevel === 'low') {
    recommendation += 'low activity, ';
  } else if (activityLevel === 'moderate') {
    recommendation += 'moderate activity, ';
  } else {
    recommendation += 'high activity, ';
  }

  if (bodyCondition === 'underweight') {
    recommendation += 'and underweight condition, your pet needs more calories to reach ideal weight.';
  } else if (bodyCondition === 'overweight') {
    recommendation += 'and overweight condition, your pet needs fewer calories to reach ideal weight.';
  } else {
    recommendation += 'and ideal body condition, maintain current feeding.';
  }

  return {
    rer,
    der,
    recommendation,
  };
}

/**
 * Validates pet form data
 */
export function validatePetData(data: Partial<PetFormData>): string[] {
  const errors: string[] = [];

  if (!data.name || data.name.trim().length < 2) {
    errors.push('Pet name must be at least 2 characters');
  }

  if (!data.species) {
    errors.push('Please select a species');
  }

  if (!data.breed || data.breed.trim().length < 2) {
    errors.push('Breed must be at least 2 characters');
  }

  if (!data.age || data.age < 0 || data.age > 30) {
    errors.push('Age must be between 0 and 30 years');
  }

  if (!data.weight || data.weight < 0.5 || data.weight > 100) {
    errors.push('Weight must be between 0.5 and 100 kg');
  }

  if (!data.activityLevel) {
    errors.push('Please select an activity level');
  }

  if (!data.bodyCondition) {
    errors.push('Please select a body condition');
  }

  return errors;
}

/**
 * Creates a new pet object with default values
 */
export function createEmptyPet(): PetFormData {
  return {
    name: '',
    species: 'dog',
    breed: '',
    age: 0,
    weight: 0,
    activityLevel: 'moderate',
    bodyCondition: 'ideal',
    allergies: [],
    specialNeeds: '',
  };
}

/**
 * Converts kg to lbs for display
 */
export function kgToLbs(kg: number): number {
  return Math.round(kg * 2.20462 * 10) / 10;
}

/**
 * Converts lbs to kg for storage
 */
export function lbsToKg(lbs: number): number {
  return Math.round((lbs / 2.20462) * 10) / 10;
}

/**
 * Gets breed suggestions based on species
 */
export function getBreedSuggestions(species: PetSpecies): string[] {
  if (species === 'dog') {
    return [
      'Labrador Retriever',
      'German Shepherd',
      'Golden Retriever',
      'French Bulldog',
      'Bulldog',
      'Beagle',
      'Poodle',
      'Rottweiler',
      'German Shorthaired Pointer',
      'Yorkshire Terrier',
      'Boxer',
      'Dachshund',
      'Siberian Husky',
      'Great Dane',
      'Doberman Pinscher',
      'Australian Shepherd',
      'Miniature Schnauzer',
      'Cavalier King Charles Spaniel',
      'Shih Tzu',
      'Boston Terrier',
      'Pomeranian',
      'Havanese',
      'Shetland Sheepdog',
      'Brittany',
      'Pembroke Welsh Corgi',
      'Mixed Breed',
    ];
  } else {
    return [
      'Domestic Shorthair',
      'Domestic Longhair',
      'Siamese',
      'Persian',
      'Maine Coon',
      'Ragdoll',
      'Bengal',
      'Abyssinian',
      'Birman',
      'Oriental Shorthair',
      'Sphynx',
      'Devon Rex',
      'American Shorthair',
      'British Shorthair',
      'Scottish Fold',
      'Russian Blue',
      'Norwegian Forest Cat',
      'Himalayan',
      'Burmese',
      'Mixed Breed',
    ];
  }
}
